/**
 * @file Alternative import/export utilities that serialize the model into pure JSON with ID references only.
 *       This co-exists with the original telejson-based module so we can gradually migrate.
 */

// @ts-ignore
import { saveAs } from 'file-saver';
import {
  ACTIONS,
  currentDiagram,
  DIAGRAM_CHANGE_SOURCES,
} from '../components/App';
import { cy } from '../components/DiagramCanvas';
import {
  DiagramTreeNode,
  diagramTreeRoot,
  importDiagramTreeRoot,
  resetDiagramTreeRoot,
} from '../model/diagram-tree-model';
import {
  EdgeArray,
  EdgeType,
  MMEdge,
  derivedEdgeArray,
  originalEdgeArray,
  importEdgeArrays,
  resetEdgeArrays,
  LinkQualifier,
  LinkNegation,
} from '../model/edge-model';
import {
  Affiliation,
  ChildGroupType,
  Essence,
  MMNode,
  MMRoot,
  importMMRoot,
  masterModelRoot,
  resetMasterModelRoot,
  type ProcessAutonomy,
} from '../model/node-model';
import { eleCounter } from './elementCounter';
import { updateFromMasterModel } from './diagram-switching';
import { buildOplItemsFromPlain } from '../utils/opm-data-adapter';
import { recalculateNamespacesForMasterModel } from '../utils/node-namespace';

const OPMCV_PERF_PREFIX = '[OPMCV-PERF]';
const opmcvNow = () =>
  typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now();
const opmcvPerfLog = (...args: any[]) =>
  console.log(OPMCV_PERF_PREFIX, ...args);

let importPlainSeq = 0;

type PlainNode = {
  id: string;
  type: MMNode['type'];
  label: string;
  // 节点描述（可选，兼容旧数据时可能不存在）
  description?: string;
  // 扩展字段（与描述同级）
  namespace?: string;
  packageName?: string;
  codeName?: string;
  tags?: string[];
  objectType?: string;
  exampleValues?: string;
  version?: string;
  updatedAt?: string;
  updatedBy?: string[];
  relatedSystem?: string;
  autonomy?: ProcessAutonomy;
  parentId: string | null;
  // 子节点分组类型（状态/值/展开子图），用于菜单显隐与 label 展示
  childGroupType?: ChildGroupType;
  essence: Essence;
  affiliation: Affiliation;
  isStructurePart: boolean;
  isSubelementOfMain: boolean;
  deleted: boolean;
  diagramId: string | null;
  // 节点尺寸字段
  width: number | null;
  height: number | null;
};

type PlainEdge = {
  id: string;
  sourceId: string;
  targetId: string;
  type: EdgeType;
  label: string;
  tags?: string[];
  description?: string;
  version?: string;
  updatedAt?: string;
  updatedBy?: string[];
  instanceFileName?: string;
  instanceFileUrl?: string;
  propagation: boolean;
  deleted: boolean;
  originalEdgeIds: string[];
  derivedEdgeIds: string[];
  preferedEdgeId: string | null;
  collection: 'original' | 'derived';
  linkQualifier: LinkQualifier;
  linkNegation: LinkNegation;
  markerColor?: string;
};

type PlainDiagram = {
  id: string;
  label: string | number;
  labelId: number;
  parentId: string | null;
  childIds: string[];
  mainNodeId: string | null;
  diagramJson: any;
  // 预生成的 OPL 语句（可选）
  opl?: ReturnType<typeof buildOplItemsFromPlain>;
};

type PlainExportPayload = {
  version: 1;
  nodes: PlainNode[];
  originalEdges: PlainEdge[];
  derivedEdges: PlainEdge[];
  diagrams: PlainDiagram[];
  rootDiagramId: string;
  eleCounter: string;
};

const collectNodes = (
  node: MMNode,
  parentId: string | null,
  nodes: PlainNode[],
  nodeMap: Map<MMNode, string>
) => {
  // 容错：避免异常空节点导致导出/OPL 生成崩溃
  if (!node) return;
  nodeMap.set(node, node.id);
  nodes.push({
    id: node.id,
    type: node.type,
    label: node.label,
    description: node.description,
    namespace: node.namespace,
    packageName: node.packageName,
    codeName: node.codeName,
    tags: node.tags || [],
    objectType: node.objectType,
    exampleValues: node.exampleValues || '',
    version: node.version,
    updatedAt: node.updatedAt,
    updatedBy: node.updatedBy,
    relatedSystem: node.relatedSystem,
    autonomy: node.type === 'process' ? node.autonomy : undefined,
    parentId,
    // 持久化子节点分组类型，避免刷新后 InZoom 父节点退化为 none。
    childGroupType: node.childGroupType,
    essence: node.essence,
    affiliation: node.affiliation,
    isStructurePart: node.isStructurePart,
    isSubelementOfMain: node.isSubelementOfMain,
    deleted: node.deleted,
    diagramId: null,
    // 导出节点尺寸
    width: null,
    height: null,
  });
  node.children.forEach(child => collectNodes(child, node.id, nodes, nodeMap));
};

const collectEdgePlain = (
  edges: MMEdge[],
  collection: 'original' | 'derived',
  edgeMap: Map<MMEdge, string>
) => {
  // 过滤掉 source 或 target 为 null 的无效边，避免导出时崩溃
  const validEdges = edges.filter(edge => {
    if (!edge || !edge.source || !edge.target) {
      console.warn(
        `[OPMCV-EXPORT] 跳过无效边: id=${edge?.id}, source=${edge?.source?.id ?? 'NULL'}, target=${edge?.target?.id ?? 'NULL'}, collection=${collection}`
      );
      return false;
    }
    return true;
  });
  return validEdges.map<PlainEdge>(edge => {
    edgeMap.set(edge, edge.id);
    return {
      id: edge.id,
      sourceId: edge.source.id,
      targetId: edge.target.id,
      type: edge.type,
      label: edge.label,
      tags: edge.tags || [],
      description: edge.description,
      version: edge.version,
      updatedAt: edge.updatedAt,
      updatedBy: edge.updatedBy,
      instanceFileName: edge.instanceFileName,
      instanceFileUrl: edge.instanceFileUrl,
      propagation: edge.propagation,
      deleted: edge.deleted,
      originalEdgeIds: edge.originalEdges.map(orig => orig.id),
      derivedEdgeIds: edge.derivedEdges.map(d => d.id),
      preferedEdgeId: edge.preferedEdge ? edge.preferedEdge.id : null,
      collection,
      linkQualifier: edge.linkQualifier,
      linkNegation: edge.linkNegation,
      markerColor: edge.originalEdges.length ? '#ccc' : '#586D8C',
    };
  });
};

const normalizeDiagramJson = (
  diagram: DiagramTreeNode,
  nodeMap: Map<MMNode, string>,
  edgeMap: Map<MMEdge, string>
) => {
  if (!diagram.diagramJson) {
    return null;
  }

  const replacer = (_key: string, value: any) => {
    if (value && typeof value === 'object') {
      if (nodeMap.has(value as MMNode)) {
        return nodeMap.get(value as MMNode);
      }
      if (edgeMap.has(value as MMEdge)) {
        return edgeMap.get(value as MMEdge);
      }
    }
    return value;
  };

  const jsonClone = JSON.parse(JSON.stringify(diagram.diagramJson, replacer));

  if (jsonClone?.elements?.nodes) {
    jsonClone.elements.nodes.forEach((node: any) => {
      if (
        node?.data &&
        Object.prototype.hasOwnProperty.call(node.data, 'MMRef')
      ) {
        node.data.MMRefId = node.data.MMRef;
        delete node.data.MMRef;
      }
    });
  }
  if (jsonClone?.elements?.edges) {
    jsonClone.elements.edges.forEach((edge: any) => {
      if (
        edge?.data &&
        Object.prototype.hasOwnProperty.call(edge.data, 'MMRef')
      ) {
        edge.data.MMRefId = edge.data.MMRef;
        delete edge.data.MMRef;
      }
    });
  }
  return jsonClone;
};

const collectDiagrams = (
  diagram: DiagramTreeNode,
  parentId: string | null,
  nodeDiagramMap: Map<string, string>,
  nodeIdLookup: Map<MMNode, string>,
  edgeIdLookup: Map<MMEdge, string>,
  output: PlainDiagram[],
  diagramIdLookup: Map<DiagramTreeNode, string>,
  counter: { value: number }
): PlainDiagram => {
  const id = `diagram-${counter.value++}`;
  diagramIdLookup.set(diagram, id);
  if (diagram.mainNode instanceof MMNode) {
    const nodeId = nodeIdLookup.get(diagram.mainNode);
    if (nodeId) {
      nodeDiagramMap.set(nodeId, id);
    }
  }
  const plain: PlainDiagram = {
    id,
    label: diagram.label as string | number,
    labelId: diagram.labelId,
    parentId,
    childIds: [],
    mainNodeId: diagram.mainNode instanceof MMNode ? diagram.mainNode.id : null,
    diagramJson: normalizeDiagramJson(diagram, nodeIdLookup, edgeIdLookup),
  };
  output.push(plain);
  diagram.children.forEach(child => {
    const childPlain = collectDiagrams(
      child,
      id,
      nodeDiagramMap,
      nodeIdLookup,
      edgeIdLookup,
      output,
      diagramIdLookup,
      counter
    );
    plain.childIds.push(childPlain.id);
  });
  return plain;
};

/**
 * 获取当前编辑器数据的 Plain 格式对象（不保存文件）
 * 可用于进一步转换为其他格式
 */
export const getPlainExportData = (): PlainExportPayload => {
  // Sync current diagram json before collecting diagrams to ensure latest view is captured.
  const json = cy.json();
  delete json.style;
  currentDiagram.diagramJson = json;

  recalculateNamespacesForMasterModel(masterModelRoot);

  const nodes: PlainNode[] = [];
  const nodeLookup = new Map<MMNode, string>();
  masterModelRoot.children.forEach(node =>
    collectNodes(node, null, nodes, nodeLookup)
  );

  const edgeLookup = new Map<MMEdge, string>();
  const originalEdges = collectEdgePlain(
    originalEdgeArray.edges,
    'original',
    edgeLookup
  );
  const derivedEdges = collectEdgePlain(
    derivedEdgeArray.edges,
    'derived',
    edgeLookup
  );
  const edgeIdMap = new Map<string, PlainEdge>();
  [...originalEdges, ...derivedEdges].forEach(edge => {
    edgeIdMap.set(edge.id, edge);
  });

  const nodeDiagramMap = new Map<string, string>();
  const plainDiagrams: PlainDiagram[] = [];
  const diagramLookup = new Map<DiagramTreeNode, string>();
  collectDiagrams(
    diagramTreeRoot,
    null,
    nodeDiagramMap,
    nodeLookup,
    edgeLookup,
    plainDiagrams,
    diagramLookup,
    { value: 0 }
  );

  nodes.forEach(plainNode => {
    if (nodeDiagramMap.has(plainNode.id)) {
      plainNode.diagramId = nodeDiagramMap.get(plainNode.id)!;
    }
  });

  plainDiagrams.forEach(diagram => {
    if (diagram.opl && diagram.opl.length) return;
    const nodeIds = new Set<string>();
    const edgeIds = new Set<string>();
    const elements = diagram.diagramJson?.elements || {};
    (elements.nodes || []).forEach((n: any) => {
      const id = n?.data?.MMRefId || n?.data?.id;
      if (id) nodeIds.add(id);
    });
    (elements.edges || []).forEach((e: any) => {
      const id = e?.data?.MMRefId || e?.data?.id;
      if (id) edgeIds.add(id);
    });
    // 容错：过滤异常空节点，避免读取 id 报错
    const diagramNodes = nodes.filter(n => n && nodeIds.has(n.id));
    const diagramEdges = Array.from(edgeIds)
      .map(id => edgeIdMap.get(id))
      .filter(Boolean) as PlainEdge[];
    diagram.opl = buildOplItemsFromPlain(diagramNodes, diagramEdges);
  });

  return {
    version: 1,
    nodes,
    originalEdges,
    derivedEdges,
    diagrams: plainDiagrams,
    rootDiagramId: diagramLookup.get(diagramTreeRoot) ?? '',
    eleCounter: eleCounter.value,
  };
};

/**
 * 导出为 Plain JSON 文件
 */
export const exportPlainJson = () => {
  const payload = getPlainExportData();
  const stringified = JSON.stringify(payload, null, 2);
  const blob = new Blob([stringified], {
    type: 'application/json;charset=utf-8',
  });
  saveAs(blob, 'graph-plain.json');
};

const restoreDiagramJson = (
  plainJson: any,
  nodeMap: Map<string, MMNode>,
  edgeMap: Map<string, MMEdge>
) => {
  if (!plainJson) {
    return null;
  }
  const clone = JSON.parse(JSON.stringify(plainJson));
  if (clone?.elements?.nodes) {
    clone.elements.nodes.forEach((node: any) => {
      const id = node?.data?.MMRefId ?? node?.data?.MMRef;
      if (id) {
        node.data.MMRef = nodeMap.get(id) ?? null;
      }
    });
  }
  if (clone?.elements?.edges) {
    // 收集所有节点 id，用于验证边的 source/target 是否存在
    const nodeIds = new Set<string>(
      (clone.elements.nodes ?? []).map((n: any) => n?.data?.id).filter(Boolean)
    );
    // 过滤掉引用了不存在节点的边，防止 Cytoscape 加载时报错
    const originalEdgeCount = clone.elements.edges.length;
    clone.elements.edges = clone.elements.edges.filter((edge: any) => {
      const source = edge?.data?.source;
      const target = edge?.data?.target;
      const valid =
        source && target && nodeIds.has(source) && nodeIds.has(target);
      if (!valid) {
        console.warn(
          `[restoreDiagramJson] 过滤无效边: id=${edge?.data?.id}, source=${source}, target=${target}`
        );
      }
      return valid;
    });
    if (clone.elements.edges.length < originalEdgeCount) {
      console.warn(
        `[restoreDiagramJson] 已过滤 ${originalEdgeCount - clone.elements.edges.length} 条无效边`
      );
    }
    clone.elements.edges.forEach((edge: any) => {
      const id = edge?.data?.MMRefId ?? edge?.data?.MMRef;
      if (id) {
        edge.data.MMRef = edgeMap.get(id) ?? null;
      }
    });
  }
  return clone;
};

export const importPlainJson = (stringJson: any, dispatch: Function) => {
  const seq = (importPlainSeq += 1);
  const totalStart = opmcvNow();
  const rawSize =
    typeof stringJson === 'string'
      ? stringJson.length
      : JSON.stringify(stringJson ?? '').length;
  opmcvPerfLog(`#IMP-${seq}`, 'importPlainJson:start', { rawSize });

  const parseStart = opmcvNow();
  const parsed: PlainExportPayload = JSON.parse(stringJson as string);
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:parse',
    `${(opmcvNow() - parseStart).toFixed(1)}ms`,
    {
      nodes: parsed.nodes?.length ?? 0,
      originalEdges: parsed.originalEdges?.length ?? 0,
      derivedEdges: parsed.derivedEdges?.length ?? 0,
      diagrams: parsed.diagrams?.length ?? 0,
    }
  );

  // 兼容旧数据：确保同一父节点下的 labelId 从 1 开始递增，避免新增子图时 key 冲突
  const normalizeDiagramStart = opmcvNow();
  const diagramGroupMap = new Map<
    string | null,
    PlainExportPayload['diagrams']
  >();
  parsed.diagrams.forEach(diagram => {
    const key = diagram.parentId ?? null;
    if (!diagramGroupMap.has(key)) diagramGroupMap.set(key, []);
    diagramGroupMap.get(key)!.push(diagram);
  });
  diagramGroupMap.forEach(diagrams => {
    diagrams
      .sort((a, b) => a.labelId - b.labelId)
      .forEach((diagram, idx) => {
        diagram.labelId = idx + 1;
      });
  });
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:normalizeDiagramLabelId',
    `${(opmcvNow() - normalizeDiagramStart).toFixed(1)}ms`
  );

  // Nodes
  const nodesStart = opmcvNow();
  const newRoot = new MMRoot();
  const nodeMap = new Map<string, MMNode>();
  const plainNodeMap = new Map(parsed.nodes.map(node => [node.id, node]));
  parsed.nodes.forEach(plain => {
    const node = new MMNode(plain.id, plain.type, plain.label);
    node.description = plain.description || '';
    node.namespace = plain.namespace || '';
    node.packageName = plain.packageName || '';
    node.codeName = plain.codeName || '';
    node.tags = Array.isArray(plain.tags) ? plain.tags : [];
    node.objectType = plain.objectType ?? node.objectType;
    node.exampleValues = plain.exampleValues || '';
    node.version = plain.version || node.version;
    node.updatedAt = plain.updatedAt || '';
    node.updatedBy = Array.isArray(plain.updatedBy) ? plain.updatedBy : [];
    node.relatedSystem = plain.relatedSystem || '';
    node.autonomy =
      plain.type === 'process'
        ? {
            action: plain.autonomy?.action || '',
            prompt: plain.autonomy?.prompt || '',
            withTool: !!plain.autonomy?.withTool,
          }
        : undefined;
    node.essence = plain.essence;
    node.affiliation = plain.affiliation;
    node.isStructurePart = plain.isStructurePart;
    node.isSubelementOfMain = plain.isSubelementOfMain;
    node.deleted = plain.deleted;
    node.children = [];
    // 兼容：导入时带回 childGroupType（若缺失则保持 None，后续会根据子节点自动推断）
    node.childGroupType = plain.childGroupType || ChildGroupType.None;
    nodeMap.set(plain.id, node);
  });
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:instantiateNodes',
    `${(opmcvNow() - nodesStart).toFixed(1)}ms`
  );

  const linkNodesStart = opmcvNow();
  parsed.nodes.forEach(plain => {
    const node = nodeMap.get(plain.id)!;
    if (plain.parentId) {
      const parentNode = nodeMap.get(plain.parentId);
      parentNode?.addChild(node);
    } else {
      newRoot.addChild(node);
    }
  });
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:linkNodeTree',
    `${(opmcvNow() - linkNodesStart).toFixed(1)}ms`
  );

  // 兼容旧数据：如果父节点已有子节点但 childGroupType 仍为 None，则自动推断
  const inferGroupStart = opmcvNow();
  nodeMap.forEach(node => {
    if (node.childGroupType && node.childGroupType !== ChildGroupType.None) {
      return;
    }
    const children = node.children || [];
    const hasValue = children.some(child => child.type === 'value');
    const hasState = children.some(child => child.type === 'state');
    const hasInZoomChild = children.some(
      child =>
        (child.type === 'object' || child.type === 'process') &&
        child.isSubelementOfMain
    );
    // Value 比 State 优先（与 computeDisplayLabel 的 [] 逻辑一致）
    if (hasValue) node.childGroupType = ChildGroupType.Value;
    else if (hasState) node.childGroupType = ChildGroupType.State;
    else if (hasInZoomChild) node.childGroupType = ChildGroupType.InZoom;
  });
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:inferChildGroupType',
    `${(opmcvNow() - inferGroupStart).toFixed(1)}ms`
  );

  recalculateNamespacesForMasterModel(newRoot);

  // Edges
  const edgesStart = opmcvNow();
  const newOriginalEdges = new EdgeArray();
  const newDerivedEdges = new EdgeArray();
  const edgeMap = new Map<string, MMEdge>();

  const instantiateEdge = (plain: PlainEdge) => {
    const source = nodeMap.get(plain.sourceId);
    const target = nodeMap.get(plain.targetId);
    if (!source || !target) {
      return null;
    }
    const edge = new MMEdge(
      plain.id,
      source,
      target,
      plain.type,
      plain.propagation,
      undefined,
      plain.label,
      plain.linkQualifier || LinkQualifier.None,
      plain.linkNegation || LinkNegation.None
    );
    edge.tags = Array.isArray(plain.tags) ? plain.tags : [];
    edge.description = plain.description || '';
    edge.version = plain.version || edge.version;
    edge.updatedAt = plain.updatedAt || '';
    edge.updatedBy = Array.isArray(plain.updatedBy) ? plain.updatedBy : [];
    edge.instanceFileName = plain.instanceFileName || '';
    edge.instanceFileUrl = plain.instanceFileUrl || '';
    edge.deleted = plain.deleted;
    edgeMap.set(plain.id, edge);
    if (plain.collection === 'original') {
      newOriginalEdges.addEdge(edge);
    } else {
      newDerivedEdges.addEdge(edge);
    }
    return edge;
  };

  parsed.originalEdges.forEach(instantiateEdge);
  parsed.derivedEdges.forEach(instantiateEdge);
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:instantiateEdges',
    `${(opmcvNow() - edgesStart).toFixed(1)}ms`
  );

  const edgeRelStart = opmcvNow();
  const applyEdgeRelations = (plainEdge: PlainEdge) => {
    const edge = edgeMap.get(plainEdge.id);
    if (!edge) return;
    edge.originalEdges = plainEdge.originalEdgeIds
      .map(id => edgeMap.get(id))
      .filter((x): x is MMEdge => Boolean(x));
    edge.derivedEdges = plainEdge.derivedEdgeIds
      .map(id => edgeMap.get(id))
      .filter((x): x is MMEdge => Boolean(x));
    edge.preferedEdge = plainEdge.preferedEdgeId
      ? (edgeMap.get(plainEdge.preferedEdgeId) ?? null)
      : null;
  };

  parsed.originalEdges.forEach(applyEdgeRelations);
  parsed.derivedEdges.forEach(applyEdgeRelations);
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:applyEdgeRelations',
    `${(opmcvNow() - edgeRelStart).toFixed(1)}ms`
  );

  // Diagrams
  const diagramsStart = opmcvNow();
  const diagramMap = new Map<string, DiagramTreeNode>();
  let restoreDiagramTotalMs = 0;
  parsed.diagrams.forEach(plain => {
    const mainNode = plain.mainNodeId
      ? (nodeMap.get(plain.mainNodeId) ?? newRoot)
      : newRoot;
    const diagram = new DiagramTreeNode(plain.label, mainNode);
    diagram.labelId = plain.labelId;
    diagram.children = [];
    diagram.parent = null;
    const restoreStart = opmcvNow();
    diagram.diagramJson = restoreDiagramJson(
      plain.diagramJson,
      nodeMap,
      edgeMap
    );
    if (diagram.diagramJson?.elements?.nodes) {
      diagram.diagramJson.elements.nodes.forEach((node: any) => {
        const id = node?.data?.MMRefId ?? node?.data?.id;
        if (!id || !node?.data) return;
        const plainNode = plainNodeMap.get(id);
        if (!plainNode) return;
        if (
          node.data.customWidth === undefined &&
          Number.isFinite(plainNode.width)
        ) {
          node.data.customWidth = plainNode.width;
        }
        if (
          node.data.customHeight === undefined &&
          Number.isFinite(plainNode.height)
        ) {
          node.data.customHeight = plainNode.height;
        }
      });
    }
    restoreDiagramTotalMs += opmcvNow() - restoreStart;
    diagramMap.set(plain.id, diagram);
    if (mainNode instanceof MMNode) {
      mainNode.diagram = diagram;
    }
  });
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:buildDiagrams',
    `${(opmcvNow() - diagramsStart).toFixed(1)}ms`,
    { restoreDiagramTotalMs: Number(restoreDiagramTotalMs.toFixed(1)) }
  );

  const linkDiagramsStart = opmcvNow();
  parsed.diagrams.forEach(plain => {
    const diagram = diagramMap.get(plain.id);
    if (!diagram) return;
    if (plain.parentId) {
      const parentDiagram = diagramMap.get(plain.parentId);
      if (parentDiagram) {
        diagram.parent = parentDiagram;
        parentDiagram.children.push(diagram);
      }
    }
  });
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:linkDiagramTree',
    `${(opmcvNow() - linkDiagramsStart).toFixed(1)}ms`
  );

  const rootDiagram = diagramMap.get(parsed.rootDiagramId) ?? diagramTreeRoot;

  const importModelStart = opmcvNow();
  importMMRoot(newRoot);
  importEdgeArrays(newOriginalEdges, newDerivedEdges);
  importDiagramTreeRoot(rootDiagram);
  eleCounter.value = parsed.eleCounter;
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:importToRuntime',
    `${(opmcvNow() - importModelStart).toFixed(1)}ms`
  );

  const cyRemoveStart = opmcvNow();
  cy.elements().remove();
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:cyRemoveAll',
    `${(opmcvNow() - cyRemoveStart).toFixed(1)}ms`
  );
  if (rootDiagram.diagramJson) {
    const cyJsonStart = opmcvNow();
    cy.json(rootDiagram.diagramJson);
    opmcvPerfLog(
      `#IMP-${seq}`,
      'phase:cyJson',
      `${(opmcvNow() - cyJsonStart).toFixed(1)}ms`
    );
  }
  const updateStart = opmcvNow();
  updateFromMasterModel();
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:updateFromMasterModel',
    `${(opmcvNow() - updateStart).toFixed(1)}ms`
  );
  const dispatchStart = opmcvNow();
  dispatch({
    type: ACTIONS.CHANGE_DIAGRAM,
    payload: rootDiagram,
    source: DIAGRAM_CHANGE_SOURCES.INITIAL_IMPORT,
  });
  dispatch({ type: ACTIONS.UPDATE_TREE });
  opmcvPerfLog(
    `#IMP-${seq}`,
    'phase:dispatch',
    `${(opmcvNow() - dispatchStart).toFixed(1)}ms`
  );
  opmcvPerfLog(
    `#IMP-${seq}`,
    'importPlainJson:done',
    `${(opmcvNow() - totalStart).toFixed(1)}ms`
  );
};

/**
 * 重置所有全局数据（新建本体图时调用，清除上一个本体图的残留数据）
 */
export const resetAllData = (dispatch: Function) => {
  // 重置所有全局状态
  resetMasterModelRoot();
  resetEdgeArrays();
  resetDiagramTreeRoot();
  eleCounter.reset();

  // 清空画布
  cy.elements().remove();

  // 通知 UI 更新
  dispatch({
    type: ACTIONS.CHANGE_DIAGRAM,
    payload: diagramTreeRoot,
    source: DIAGRAM_CHANGE_SOURCES.RESET_ALL_DATA,
  });
  dispatch({ type: ACTIONS.UPDATE_TREE });

  console.log('✅ 已重置所有数据，准备新建本体图');
};
